import { Address } from 'viem'
import { GetManagedVaultData, GetVaultData } from '../query'
import { ExtendedConfig } from '../types'
import { useManagedVaults } from './useManagedVaults'
import { useVault } from './useVault'

export enum GenericVaultType {
  Managed = 'managed',
  Classic = 'classic',
}

// @ts-ignore - Unused type parameter
type UseGenericVaultProps<config extends ExtendedConfig, selectData = GetVaultData | GetManagedVaultData> = {
  address?: Address
  type?: GenericVaultType
  query?: {
    enabled?: boolean
    select?: (data: GetVaultData | GetManagedVaultData) => selectData
    refetchOnMount?: boolean
  }
}

export type GetGenericVaultData = GetVaultData | GetManagedVaultData

export function isClassicVault(data: GetGenericVaultData | undefined): data is GetVaultData {
  return !!data && !('pendingGuardian' in data)
}
export function isManagedVault(data: GetGenericVaultData | undefined): data is GetManagedVaultData {
  return !!data && 'pendingGuardian' in data
}

export function useGenericVault<config extends ExtendedConfig, selectData = GetGenericVaultData>({
  address,
  type = GenericVaultType.Classic,
  query,
}: UseGenericVaultProps<config, selectData>) {
  // Use standard vault hook
  const standardResult = useVault<config, selectData>({
    address,
    query: {
      select: query?.select as (data: GetVaultData) => selectData,
      enabled: type === GenericVaultType.Classic && query?.enabled !== false,
      refetchOnMount: query?.refetchOnMount,
    },
  })

  // Use managed vault hook
  const managedResult = useManagedVaults({
    queries: address
      ? [
          {
            address,
            query: {
              ...query,
              enabled: type === GenericVaultType.Managed && query?.enabled !== false,
              refetchOnMount: query?.refetchOnMount,
            },
          },
        ]
      : [],
    combine: (results) => results[0],
    select: query?.select as (data: GetManagedVaultData) => selectData,
  })

  // Return appropriate result based on type
  return type === GenericVaultType.Managed ? managedResult : standardResult
}
